import { defineComponent, ref, computed } from 'vue-demi';
import { s as script$1 } from './OrButton-78a118d7.js';
import { a as BUTTON_COLOR } from './OrIconButton-ad0cbc8d.js';
import { s as script$2, O as OrModalSizes } from './OrModal-f3a4813a.js';
import { s as script$3 } from './OrInput-9be9a209.js';
import { useMagicKeys, useActiveElement, whenever } from '@vueuse/core';
import { logicAnd } from '@vueuse/math';
import { resolveComponent, openBlock, createBlock, withCtx, renderSlot, createElementVNode, createVNode, createCommentVNode } from 'vue';
import { s as styleInject } from './style-inject.es-4c6f2515.js';

var script = defineComponent({
  name: 'OrConfirm',
  components: {
    OrButton: script$1,
    OrModal: script$2,
    OrInput: script$3
  },
  props: {
    titleText: {
      type: String,
      required: false,
      default: null
    },
    confirmButtonText: {
      type: String,
      required: false,
      default: 'Confirm'
    },
    denyButtonText: {
      type: String,
      required: false,
      default: 'Deny'
    },
    confirmButtonColor: {
      type: String,
      required: false,
      default: BUTTON_COLOR.ERROR
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    confirmationText: {
      type: String,
      required: false,
      default: null
    },
    textConfirmationLabel: {
      type: String,
      required: false,
      default: null
    },
    textConfirmationPlaceholder: {
      type: String,
      required: false,
      default: null
    },
    textConfirmationError: {
      type: String,
      required: false,
      default: null
    },
    showClose: {
      type: Boolean,
      required: false,
      default: false
    },
    closeOnClickOutside: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: OrModalSizes.tiny
    }
  },
  emits: ['close', 'confirm'],
  setup(props, _ref) {
    let {
      emit
    } = _ref;
    const confirmationTextboxRef = ref();
    const confirmationButtonRef = ref();
    const confirmationValue = ref('');
    const {
      escape,
      enter
    } = useMagicKeys();
    const activeElement = useActiveElement();
    const isTextboxActive = computed(() => {
      var _a;
      return (activeElement === null || activeElement === void 0 ? void 0 : activeElement.value) === ((_a = confirmationTextboxRef === null || confirmationTextboxRef === void 0 ? void 0 : confirmationTextboxRef.value) === null || _a === void 0 ? void 0 : _a.inputRef);
    });
    const isInvalid = computed(() => {
      return !!props.confirmationText && props.confirmationText !== confirmationValue.value;
    });
    const handleConfirm = () => {
      if (!props.isDisabled && !isInvalid.value) emit('confirm');
    };
    whenever(logicAnd(enter, isTextboxActive), () => {
      handleConfirm();
    });
    return {
      confirmationTextboxRef,
      confirmationButtonRef,
      confirmationValue,
      isInvalid,
      escape,
      handleConfirm
    };
  },
  data() {
    return {
      touched: false
    };
  },
  computed: {
    isButtonDisabled() {
      return !!this.isDisabled || this.isInvalid;
    }
  },
  watch: {
    escape(val) {
      if (val) this.handleClose();
    },
    isOpen() {
      this.confirmationValue = '';
      this.touched = false;
    },
    confirmationTextboxRef(val) {
      if (val) val.focus();
    },
    confirmationButtonRef(val) {
      var _a;
      if (val && !this.confirmationText) (_a = val.$el) === null || _a === void 0 ? void 0 : _a.focus();
    }
  },
  methods: {
    touch() {
      this.touched = true;
    },
    handleClose() {
      if (this.isOpen) this.$emit('close');
    }
  }
});

const _hoisted_1 = {
  class: "or-confirm-footer"
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_or_input = resolveComponent("or-input");
  const _component_or_button = resolveComponent("or-button");
  const _component_or_modal = resolveComponent("or-modal");
  return openBlock(), createBlock(_component_or_modal, {
    "is-open": _ctx.isOpen,
    role: "alertdialog",
    "title-text": _ctx.titleText,
    size: _ctx.size,
    "show-close": _ctx.showClose,
    "close-on-click-outside": _ctx.closeOnClickOutside,
    class: "or-confirm",
    onClose: _ctx.handleClose
  }, {
    footer: withCtx(() => [renderSlot(_ctx.$slots, "footer", {
      close: _ctx.handleClose,
      confirm: _ctx.handleConfirm
    }, () => [createElementVNode("div", _hoisted_1, [createVNode(_component_or_button, {
      ref: "confirmationButtonRef",
      color: _ctx.confirmButtonColor,
      content: _ctx.confirmButtonText,
      "is-loading": _ctx.isLoading,
      "is-disabled": _ctx.isButtonDisabled,
      onClick: _ctx.handleConfirm
    }, null, 8 /* PROPS */, ["color", "content", "is-loading", "is-disabled", "onClick"]), createVNode(_component_or_button, {
      type: "outlined",
      content: _ctx.denyButtonText,
      "is-disabled": _ctx.isLoading,
      onClick: _ctx.handleClose
    }, null, 8 /* PROPS */, ["content", "is-disabled", "onClick"])])])]),
    default: withCtx(() => [renderSlot(_ctx.$slots, "default"), _ctx.confirmationText ? (openBlock(), createBlock(_component_or_input, {
      key: 0,
      ref: "confirmationTextboxRef",
      modelValue: _ctx.confirmationValue,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => _ctx.confirmationValue = $event),
      class: "confirmation-textbox",
      label: _ctx.textConfirmationLabel,
      placeholder: _ctx.textConfirmationPlaceholder,
      error: _ctx.textConfirmationError,
      "is-invalid": _ctx.touched && _ctx.isInvalid,
      onBlur: _ctx.touch
    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error", "is-invalid", "onBlur"])) : createCommentVNode("v-if", true)]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["is-open", "title-text", "size", "show-close", "close-on-click-outside", "onClose"]);
}

var css_248z = "/**\n * A simple way to manage mobile breakpoints described in https://davidwalsh.name/write-media-queries-sass.\n * Can be further extended using following approach https://rimdev.io/making-media-query-mixins-with-sass/\n */\n@keyframes spinAround-077d7158 {\nfrom {\n    transform: rotate(0);\n}\nto {\n    transform: rotate(359deg);\n}\n}\n@keyframes fade-077d7158 {\nfrom {\n    opacity: 1;\n}\nto {\n    opacity: 0;\n}\n}\n.or-confirm-footer[data-v-077d7158] {\n  display: flex;\n  flex-direction: row;\n  gap: var(--s-4);\n  justify-content: flex-end;\n}\n.confirmation-textbox[data-v-077d7158] {\n  margin-top: var(--s-3);\n}\n.or-modal[data-v-077d7158] .header {\n  padding-bottom: var(--s-4) !important;\n}\n.or-modal[data-v-077d7158] .content {\n  padding-bottom: 0 !important;\n  font-size: var(--fs-1);\n  font-weight: normal;\n  line-height: var(--lh-1);\n}\n.or-modal[data-v-077d7158] .footer {\n  box-shadow: none !important;\n}";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-077d7158";
script.__file = "src/components/or-confirm/OrConfirm.vue";

export { script as s };
